import { Controller } from 'stimulus';
import { t } from '../javascript/lib/i18n';

export default class extends Controller {
  static targets = [
    'deviceId',
    'status',
    'error',
    'nameField',
    'deviceModelWrapper',
    'deviceModelField',
    'lockable',
    'lookupButton',
    'submitButton',
  ];
  static values = {
    path: String,
    notFoundText: String,
    unmappedText: String,
  };

  connect() {
    this.lookupAttempted = false;
    this.abortController = null;
    const deviceId = this.deviceIdTarget.value.trim();
    if (deviceId === '') {
      // Initial state: fields locked, no error until the user attempts a lookup.
      this.setLocked(true);
      this.hideDeviceModel();
      this.hideStatus();
      this.hideError();
    } else {
      this.lookupAttempted = true;
      this.validate(deviceId);
    }
  }

  disconnect() {
    this.cancelInFlightRequest();
  }

  // Disabled fields do not submit their values. Re-enable everything
  // the controller locked before the form submits, so `device_model_id`,
  // `name`, and any locked settings post correctly.
  unlockForSubmit() {
    this.lockableTargets.forEach((el) => {
      el.disabled = false;
    });
    if (this.hasDeviceModelFieldTarget) {
      this.deviceModelFieldTarget.disabled = false;
    }
  }

  clicked() {
    const deviceId = this.deviceIdTarget.value.replace(/\s/g, '');
    this.deviceIdTarget.value = deviceId;
    this.lookupAttempted = true;

    if (deviceId === '') {
      this.applyLookupFailure();
      return;
    }

    this.validate(deviceId);
  }

  deviceIdChanged() {
    // Invalidate any in-flight request — its response would be for a
    // device ID that no longer matches the input.
    this.cancelInFlightRequest();
    this.hideStatus();
    this.setLocked(true);
    this.hideDeviceModel();

    if (this.lookupAttempted && this.deviceIdTarget.value.trim() === '') {
      this.showError(this.notFoundTextValue);
    } else {
      this.hideError();
    }
  }

  async validate(deviceId) {
    // Abort any previous in-flight request so stale responses don't
    // overwrite the state of a newer one.
    this.cancelInFlightRequest();

    const controller = new AbortController();
    this.abortController = controller;

    this.hideError();
    this.showStatus(t('js.belle_device_lookup.checking'), 'info');
    this.setLookupButtonDisabled(true);

    const url = new URL(this.pathValue, window.location.href);
    url.searchParams.set('device_id', deviceId);

    try {
      const response = await fetch(url.toString(), {
        method: 'GET',
        headers: this.headers,
        signal: controller.signal,
      });

      const json = response.ok ? await response.json() : null;

      if (json && json.valid) {
        this.applyLookupSuccess(json);
      } else {
        this.applyLookupFailure();
      }
    } catch (error) {
      if (error.name === 'AbortError') return;
      this.applyLookupFailure();
    } finally {
      // Only this request should clear the in-flight marker / re-enable
      // the button — a newer request that aborted us owns those now.
      if (this.abortController === controller) {
        this.abortController = null;
        this.setLookupButtonDisabled(false);
      }
    }
  }

  cancelInFlightRequest() {
    if (this.abortController) {
      this.abortController.abort();
      this.abortController = null;
      this.setLookupButtonDisabled(false);
    }
  }

  setLookupButtonDisabled(disabled) {
    if (this.hasLookupButtonTarget) {
      this.lookupButtonTarget.disabled = disabled;
    }
  }

  applyLookupSuccess(json) {
    // Lookup found the device but we have no DeviceModel mapping for this
    // device_type yet. Keep the form locked so we don't silently save the
    // wrong device_model_id, and show a distinct error.
    if (!json.device_model_id) {
      this.applyUnmappedLookup();
      return;
    }

    this.hideStatus();
    this.hideError();
    this.setLocked(false);
    this.showDeviceModel(json.device_model_id);
    this.prefillName(json.name);
  }

  applyUnmappedLookup() {
    this.hideStatus();
    this.setLocked(true);
    this.hideDeviceModel();
    this.showError(this.unmappedTextValue);
  }

  // Populate the display name from the Belle API only when empty so we
  // never clobber an existing or user-edited value.
  prefillName(name) {
    if (!name) return;
    if (!this.hasNameFieldTarget) return;
    if (this.nameFieldTarget.value.trim() !== '') return;
    this.nameFieldTarget.value = name;
  }

  applyLookupFailure() {
    this.hideStatus();
    this.setLocked(true);
    this.hideDeviceModel();
    this.showError(this.notFoundTextValue);
  }

  setLocked(locked) {
    this.lockableTargets.forEach((el) => {
      el.disabled = locked;
    });
    // Both new and edit forms wire up the submit-button target, so that a
    // failed lookup disables the save button in either context. Edit forms
    // render the button enabled initially, since the existing device ID
    // is assumed valid until the in-flight lookup says otherwise.
    if (this.hasSubmitButtonTarget) {
      this.submitButtonTarget.disabled = locked;
    }
  }

  showDeviceModel(deviceModelId) {
    if (this.hasDeviceModelWrapperTarget) {
      this.deviceModelWrapperTarget.classList.remove('d-none');
    }
    if (this.hasDeviceModelFieldTarget) {
      if (deviceModelId) {
        this.deviceModelFieldTarget.value = String(deviceModelId);
      }
      this.deviceModelFieldTarget.disabled = true;
    }
  }

  hideDeviceModel() {
    if (this.hasDeviceModelWrapperTarget) {
      this.deviceModelWrapperTarget.classList.add('d-none');
    }
    if (this.hasDeviceModelFieldTarget) {
      this.deviceModelFieldTarget.value = '';
    }
  }

  showError(text) {
    if (this.hasErrorTarget) {
      this.errorTarget.textContent = text;
      this.errorTarget.classList.remove('d-none');
    }
  }

  hideError() {
    if (this.hasErrorTarget) {
      this.errorTarget.classList.add('d-none');
    }
  }

  showStatus(message, type) {
    if (this.hasStatusTarget) {
      this.statusTarget.textContent = message;
      this.statusTarget.className = `small text-${type} mt-1`;
    }
  }

  hideStatus() {
    if (this.hasStatusTarget) {
      this.statusTarget.textContent = '';
      this.statusTarget.className = '';
    }
  }

  get headers() {
    return {
      'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')
        ?.content,
      'Content-type': 'application/json; charset=UTF-8',
    };
  }
}
